import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { LanguageService } from '../../../services/i18n/language';
import { UserService, UserDto } from '../../../services/api/user';
import { AuthService } from '../../../services/auth/auth';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-user-view',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './user-view.html',
  styleUrl: './user-view.scss'
})
export class UserViewComponent implements OnInit, OnDestroy {
  user: UserDto | null = null;
  loading = false;
  error: string | null = null;
  deleting = false;

  // Translations
  titleText = 'Просмотр пользователя';
  backText = 'Назад к списку';
  editText = 'Редактировать';
  deleteText = 'Удалить';
  confirmDeleteText = 'Удалить пользователя? Это действие нельзя отменить.';
  deleteErrorText = 'Не удалось удалить пользователя';
  cancelText = 'Отмена';
  deletingText = 'Удаляем...';
  nameText = 'Имя';
  emailText = 'Email';
  phoneText = 'Телефон';
  roleText = 'Роль';
  commentText = 'Комментарий';
  createdAtText = 'Дата создания';
  updatedAtText = 'Дата обновления';
  noCommentText = 'Нет комментария';
  loadingText = 'Загрузка...';
  roleNotSpecifiedText = 'Не указана';
  errorNotFoundText = 'Пользователь не найден';
  errorForbiddenText = 'Ошибка доступа (403). Проверьте, что вы авторизованы как администратор.';
  errorSessionExpiredText = 'Сессия истекла. Пожалуйста, войдите снова.';
  errorLoadDefaultText = 'Ошибка при загрузке пользователя';

  private subscriptions = new Subscription();

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private languageService: LanguageService,
    private userService: UserService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (typeof window === 'undefined' || !window.localStorage) {
      console.warn('UserView: Running on server (SSR), skipping initialization');
      return;
    }

    this.updateTranslations();
    this.subscriptions.add(
      this.languageService.getTranslations().subscribe(() => {
        this.updateTranslations();
        this.cdr.markForCheck();
      })
    );

    setTimeout(() => {
      if (!this.authService.isAuthenticated()) {
        console.warn('UserView: User not authenticated, redirecting to login');
        this.router.navigate(['/login']);
        return;
      }

      const user = this.authService.getUser();
      if (user?.role?.code !== 'ROLE_ADMIN') {
        console.warn('UserView: User is not admin, redirecting to home');
        this.router.navigate(['/']);
        return;
      }

      const id = this.route.snapshot.paramMap.get('id');
      if (id) {
        this.loadUser(+id);
      }
    }, 0);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private t(key: string, fallback: string): string {
    const value = this.languageService.translate(key);
    return !value || value === key ? fallback : value;
  }

  private updateTranslations(): void {
    this.titleText = this.t('admin.userView.title', 'Просмотр пользователя');
    this.backText = this.t('admin.userView.back', 'Назад к списку');
    this.editText = this.t('admin.users.edit', 'Редактировать');
    this.deleteText = this.t('admin.users.delete', 'Удалить');
    this.confirmDeleteText = this.t('admin.userView.confirmDelete', 'Удалить пользователя? Это действие нельзя отменить.');
    this.deleteErrorText = this.t('admin.userView.deleteError', 'Не удалось удалить пользователя');
    this.cancelText = this.t('common.cancel', 'Отмена');
    this.deletingText = this.t('myCalcs.deleting', 'Удаляем...');
    this.nameText = this.t('admin.users.name', 'Имя');
    this.emailText = this.t('admin.users.email', 'Email');
    this.phoneText = this.t('admin.users.phone', 'Телефон');
    this.roleText = this.t('admin.users.role', 'Роль');
    this.commentText = this.t('admin.userView.comment', 'Комментарий');
    this.createdAtText = this.t('common.createdAt', 'Дата создания');
    this.updatedAtText = this.t('admin.userView.updatedAt', 'Дата обновления');
    this.noCommentText = this.t('admin.userView.noComment', 'Нет комментария');
    this.loadingText = this.t('admin.users.loading', 'Загрузка...');
    this.roleNotSpecifiedText = this.t('admin.userView.roleNotSpecified', 'Не указана');
    this.errorNotFoundText = this.t('admin.userView.errorNotFound', 'Пользователь не найден');
    this.errorForbiddenText = this.t('admin.userView.errorForbidden', 'Ошибка доступа (403). Проверьте, что вы авторизованы как администратор.');
    this.errorSessionExpiredText = this.t('admin.userView.errorSessionExpired', 'Сессия истекла. Пожалуйста, войдите снова.');
    this.errorLoadDefaultText = this.t('admin.userView.errorLoadDefault', 'Ошибка при загрузке пользователя');
  }

  private loadUser(id: number): void {
    this.loading = true;
    this.error = null;

    this.subscriptions.add(
      this.userService.getUser(id).subscribe({
        next: (user) => {
          this.user = user;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('UserView: Error loading user', err);
          if (err.status === 404) {
            this.error = this.errorNotFoundText;
          } else if (err.status === 403) {
            this.error = this.errorForbiddenText;
          } else if (err.status === 401) {
            this.error = this.errorSessionExpiredText;
          } else {
            this.error = this.errorLoadDefaultText;
          }
          this.loading = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  editUser(): void {
    if (this.user?.id) {
      this.router.navigate(['/admin/users', this.user.id, 'edit']);
    }
  }

  /** Подтверждение удаления прямо на странице: системный confirm() блокируют встроенные браузеры. */
  confirmDeleteOpen = false;
  deleteError: string | null = null;

  /** Удаление — здесь, а не в списке: сначала видно, кого удаляешь. */
  deleteUser(): void {
    if (!this.user?.id || this.deleting) {
      return;
    }
    this.deleteError = null;
    this.confirmDeleteOpen = true;
  }

  confirmDelete(): void {
    if (!this.user?.id || this.deleting) {
      return;
    }
    this.deleting = true;
    this.deleteError = null;
    this.subscriptions.add(
      this.userService.deleteUser(this.user.id).subscribe({
        next: () => this.router.navigate(['/admin/users']),
        error: (err) => {
          console.error('UserView: Error deleting user', err);
          this.deleting = false;
          this.deleteError = err?.error?.message || this.deleteErrorText;
          this.cdr.markForCheck();
        }
      })
    );
  }

  get initials(): string {
    return (this.user?.fullName || '?').trim().split(/\s+/).slice(0, 2).map(p => p[0]?.toUpperCase() ?? '').join('');
  }

  formatDate(dateString: string | undefined): string {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getRoleName(role: any): string {
    return role?.name || this.roleNotSpecifiedText;
  }
}
